import { type Page } from "../../types/content.ts";
import { type NormalizedSpec } from "../openapi/types.ts";

/** Um resultado da busca — guia ou endpoint, mesma forma para a lista. */
interface SearchItem {
  kind: "guide" | "endpoint";
  id: string;
  title: string;
  subtitle: string;
  to: string;
  haystack: string;
  icon: string;
  /** Só em guias. */
  section?: string;
  /** Só em endpoints — pinta a etiqueta do verbo na lista. */
  method?: string;
}

import { useEffect, useMemo, useRef, useState } from "react";

import { useNavigate } from "react-router-dom";

import { locales, nav } from "../../docs.config.js";
import { isSectionVisible, pages, sectionConfig } from "../lib/content.js";
import { useLocale } from "../lib/i18n.jsx";
import { methodClass } from "../lib/methods.js";
import { loadSpec } from "../openapi/useOpenApi.js";
import Icon from "../theme/Icon.jsx";

const apiSectionId = nav.find((n) => n.type === "openapi")?.id;

// Busca em um índice montado no cliente. Não há servidor: o site é estático, e
// o conteúdo cabe de sobra na memória (títulos e paths, não corpo de texto).
function buildIndex(locale: string, spec: NormalizedSpec | null, tagLabel: (tag: string) => string): SearchItem[] {
  const guideEntries = pages
    // Página de seção desligada ou oculta não é resultado de busca — achar pelo
    // ⌘K uma tela que não está no menu é o mesmo que ela não estar desligada.
    .filter((p) => isSectionVisible(p.section) && !p.hidden)
    .filter((p) => p.locale === locale || p.locale === locales.fallback)
    // Página traduzida vence a do fallback quando as duas existem.
    .reduce((acc, p) => {
      const key = `${p.section}/${p.slug}`;
      if (!acc.has(key) || p.locale === locale) acc.set(key, p);
      return acc;
    }, new Map<string, Page>());

  const guides: SearchItem[] = [...guideEntries.values()].map((p): SearchItem => {
    const section = sectionConfig(p.section);
    return {
      kind: "guide" as const,
      id: p.file,
      title: p.title,
      subtitle: p.description || "",
      section: p.section,
      to: p.slug ? `/${locale}/${p.section}/${p.slug}` : `/${locale}/${p.section}`,
      haystack: [p.title, p.description, p.section, ...(p.keywords || [])].filter(Boolean).join(" ").toLowerCase(),
      icon: section?.icon || "book",
    };
  });

  const apiVisible = apiSectionId && isSectionVisible(apiSectionId);
  const endpoints: SearchItem[] = (apiVisible ? (spec?.operations ?? []) : []).map((op) => ({
    kind: "endpoint" as const,
    id: op.id,
    title: op.title,
    subtitle: `${op.method} ${op.path}`,
    method: op.method,
    to: `/${locale}/${apiSectionId}/${op.tagSlug}#${op.id}`,
    // O rótulo traduzido entra no índice junto com a tag crua: os endpoints têm
    // summary em inglês, e sem isso buscar "disponibilidade" não acharia nada.
    haystack: [op.title, op.path, op.method, op.tag, op.tag ? tagLabel(op.tag) : "", op.desc]
      .filter(Boolean)
      .join(" ")
      .toLowerCase(),
    icon: "code",
  }));

  return [...guides, ...endpoints];
}

export default function Search({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { locale, t, tagLabel } = useLocale();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const [spec, setSpec] = useState<NormalizedSpec | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // O spec só é buscado quando a busca abre pela primeira vez — quem nunca abre
  // a paleta não paga o download.
  useEffect(() => {
    if (!open || spec) return;
    let alive = true;
    loadSpec()
      .then((s) => alive && setSpec(s))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [open, spec]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setCursor(0);
      // O input só existe depois deste render.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const index = useMemo(() => buildIndex(locale, spec, tagLabel), [locale, spec, tagLabel]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return index.slice(0, 8);
    const terms = q.split(/\s+/);
    return index
      .map((item) => {
        if (!terms.every((term) => item.haystack.includes(term))) return null;
        // Casar no título vale mais que casar em qualquer lugar do registro.
        const score = item.title.toLowerCase().includes(q) ? 0 : 1;
        return { item, score };
      })
      .filter((r): r is { item: SearchItem; score: number } => r !== null)
      .sort((a, b) => a.score - b.score)
      .slice(0, 20)
      .map((r) => r.item);
  }, [index, query]);

  useEffect(() => {
    setCursor(0);
  }, [query]);

  const go = (item: SearchItem | undefined) => {
    if (!item) return;
    navigate(item.to);
    onClose();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(results[cursor]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div className="cmdk-backdrop" onMouseDown={onClose} role="presentation">
      <div
        className="cmdk"
        role="dialog"
        aria-modal="true"
        aria-label={t("search")}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="cmdk-input">
          <Icon name="search" size={17} />
          <input
            ref={inputRef}
            value={query}
            onKeyDown={onKeyDown}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("searchPlaceholder")}
          />
          <kbd>esc</kbd>
        </div>

        <div className="cmdk-results">
          {results.map((item, i) => (
            <button
              key={`${item.kind}:${item.id}`}
              type="button"
              className={`cmdk-item ${i === cursor ? "active" : ""}`}
              onMouseEnter={() => setCursor(i)}
              onClick={() => go(item)}
            >
              {item.method ? (
                <span className={`ref-method-mini ${methodClass(item.method)}`}>{item.method}</span>
              ) : (
                <Icon name={item.icon} size={15} />
              )}
              <span className="cmdk-item-body">
                <span className="cmdk-item-title">{item.title}</span>
                {item.subtitle && <span className="cmdk-item-sub mono">{item.subtitle}</span>}
              </span>
              <Icon name="arrow-right" size={14} />
            </button>
          ))}
          {!results.length && <div className="cmdk-empty mono">{t("nothingFound")}</div>}
        </div>
      </div>
    </div>
  );
}
